import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import useMembers from '../../hooks/useMembers';
import useViewerRole from '../../hooks/useViewerRole';
import { canManageMembers } from '../../lib/permissions';
import { paths } from '../../routes/paths';
import PageHeader from '../../components/ui/PageHeader/PageHeader';
import Button from '../../components/ui/Button/Button';
import Input from '../../components/ui/Input/Input';
import Icon from '../../components/ui/Icon/Icon';
import EmptyState from '../../components/ui/EmptyState/EmptyState';
import styles from './WorkspaceSettingsPage.module.css';

const ROLES = ['admin', 'editor', 'viewer'];

export default function WorkspaceSettingsPage() {
  const { workspaceId } = useParams();
  const { members, loading, error, addMember, updateRole, removeMember } =
    useMembers(workspaceId);
  const { role: viewerRole } = useViewerRole(workspaceId);
  const canManage = canManageMembers(viewerRole);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleAdd = async (e) => {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      await addMember(email.trim(), role);
      setEmail('');
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Link to={paths.workspace(workspaceId)} className={styles.back}>
        back to workspace
      </Link>
      <PageHeader
        kicker="settings // members"
        title="ACL CONFIG"
        subtitle="// members & permissions"
      />

      {error ? (
        <EmptyState
          icon="ShieldAlert"
          title="Cannot manage members"
          description={`${error} — admin or owner access required.`}
        />
      ) : (
        <>
          {canManage && (
            <form className={styles.addForm} onSubmit={handleAdd}>
              <Input
                label="invite by email"
                placeholder="person@example.com"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={formError}
              />
              <div className={styles.roleField}>
                <label className={styles.roleLabel}>role</label>
                <select
                  className={styles.select}
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
              <Button
                type="submit"
                loading={submitting}
                leftIcon={<Icon name="UserPlus" size={14} />}
              >
                Add member
              </Button>
            </form>
          )}

          <div className={styles.divider}>members</div>

          {loading ? (
            <p className={styles.loading}>loading members</p>
          ) : (
            <ul className={styles.list}>
              {members.map((m) => (
                <li key={m.userId} className={styles.row}>
                  <div className={styles.who}>
                    {m.avatarUrl ? (
                      <img src={m.avatarUrl} alt={m.name} className={styles.avatar} />
                    ) : (
                      <span className={styles.avatarFallback}>
                        {(m.name || m.email || '?').charAt(0).toUpperCase()}
                      </span>
                    )}
                    <div>
                      <div className={styles.name}>{m.name || m.email}</div>
                      <div className={styles.email}>{m.email}</div>
                    </div>
                  </div>

                  {m.role === 'owner' ? (
                    <span className={styles.ownerBadge}>owner</span>
                  ) : canManage ? (
                    <div className={styles.actions}>
                      <select
                        className={styles.select}
                        value={m.role}
                        onChange={(e) => updateRole(m.userId, e.target.value)}
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                      <Button
                        variant="ghost"
                        leftIcon={<Icon name="Trash2" size={14} />}
                        onClick={() => removeMember(m.userId)}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <span className={styles.ownerBadge}>{m.role}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </>
  );
}
